import { css } from 'lit';
import { isValidNumber } from '@/utils/utils';
import { Color } from '@typings/shared-types';
import { CSSResultArray, LitElement, PropertyValues, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { FrameDto } from '../../../typings/api';
import scss from '../../utils/scssAsCss';

export const multitagNewLineToBr = (string: string): string =>
	string.replace(/\[(nl|NL)(\d*)\]/g, '<br />');
export const multitagIsJustifyLineCenter = (string: string): boolean =>
	string.includes('[jl3]') || string.includes('[JL3]');
export const multitagIsJustifyPageMiddle = (string: string): boolean =>
	string.includes('[jp3]') || string.includes('[JP3]');
export const stripPrependedMultitags = (string: string): string =>
	string.split(']').reverse().shift()!; //	there's no way for shift() to come across an empty array, it will always have at least one entry after splitting and reversing.

export const FramePageJustificationRegex = /\[(jp|JP)(\d*)\]/g;
export const FrameLineJustificationRegex = /\[(jl|JL)(\d*)\]/g;
const FrameNewPageRegex = /\[(np|NP)]/g;
export const FrameNewLineRegex = /\[(nl|NL)(\d*)\]/g;
const FramePageTimeRegex = /\[(pt|PT)(\d*)(o|O)(\d*)\]/g;
const FrameColorRegex = /\[(cf|CF)(\d*)\]/g;
const SingleSpaceRegex = / /g;

export const frameTextToString = (frameText?: string): string => {
	return (
		frameText
			?.replace(/\[(nl|NL)(\d*)]/g, '\n')
			.split(']')
			.reverse()
			.shift() ?? ''
	);
};

export const frameMultitagTextToTemplateResult = (frameText: string): TemplateResult => {
	const containerClassMap = {
		'frame': true,
		'justify-page-top': false,
		'justify-page-middle': false,
		'justify-page-bottom': false,
		'justify-line-left': false,
		'justify-line-center': false,
		'justify-line-right': false,
	};
	const frameHTML = frameText
		//  replaces raw spaces with non-breaking spaces for text alignment
		.replaceAll(SingleSpaceRegex, '&nbsp;')
		//	strip out all newpage tags
		.replaceAll(FrameNewPageRegex, '')
		//  strip out page time tags
		.replaceAll(FramePageTimeRegex, '')
		//  strip out color tags
		.replaceAll(FrameColorRegex, '')
		//	replace page justification tags with class names for container
		.replaceAll(FramePageJustificationRegex, (_, __, justificationTypeMatch: string) => {
			if (justificationTypeMatch !== undefined) {
				const justificationType = parseInt(justificationTypeMatch, 10);
				switch (justificationType) {
					case 2:
						containerClassMap['justify-page-top'] = true;
						break;
					case 4:
						containerClassMap['justify-page-bottom'] = true;
						break;
					case 3:
					default:
						//	default vertical align to middle
						containerClassMap['justify-page-middle'] = true;
						break;
				}
			}
			return '';
		})
		//	replace line justification tags with class names for container
		.replaceAll(FrameLineJustificationRegex, (_, __, justificationTypeMatch: string) => {
			if (justificationTypeMatch) {
				const justificationType = parseInt(justificationTypeMatch, 10);
				switch (justificationType) {
					case 2:
						containerClassMap['justify-line-left'] = true;
						break;
					case 4:
						containerClassMap['justify-line-right'] = true;
						break;
					case 3:
					default:
						//	default horizontal align to center
						containerClassMap['justify-line-center'] = true;
						break;
				}
			}
			return '';
		})
		//	append custom-sized line breaks to all newline tags, mark borders with [break]
		.replaceAll(FrameNewLineRegex, (_, lineHeightMatch) => {
			let lineHeight = lineHeightMatch !== '' ? parseInt(lineHeightMatch as string, 10) : 2;
			lineHeight = Number.isNaN(lineHeight) ? 2 : lineHeight;

			return `[break]<span class="fake-break" style="height: ${lineHeight}px;"><br /></span>[break]`;
		})
		//	split by [break]
		.split('[break]')
		//	 if this line is already a break, leave it alone, otherwise wrap it in a span
		.map((line) => (line.includes('<br />') ? line : `<span class="text-line">${line}</span>`))
		//	and we're done!
		.join('');
	return html` <div class=${classMap(containerClassMap)}>${unsafeHTML(frameHTML)}</div> `;
};

@customElement('dms-frame')
export default class DMSFrame extends LitElement {
	@property({ type: Object }) public frame?: FrameDto;

	@property({ type: Boolean, reflect: true }) public loading?: boolean;

	@property({ type: Boolean, reflect: true }) public error?: boolean;

	@property({ type: Boolean, reflect: true, attribute: 'useredtext' }) public useRedText = false;

	@property({ type: String }) public image?: string;

	@property({ type: String }) public color?: Color;

	public static override get styles(): CSSResultArray {
		return [
			css`
:host {
  display: flex;
  height: 4.5em;
  line-height: 1.5em;
  padding: 1ch;
  border-radius: 6px;
  text-transform: uppercase;
  color: rgb(var(--brand-dms-text));
  text-shadow: 0px 0px 10px rgba(var(--brand-dms-text), 0.5);
  background: rgb(var(--brand-grey-darkest));
  font-family: "Courier New", monospace;
}
@media print {
  :host {
    color: rgb(var(--brand-status-danger));
  }
}

:host([useredtext]) {
  color: rgb(var(--brand-status-danger));
  text-shadow: 0px 0px 10px rgba(var(--brand-status-danger), 0.5);
}

:host([loading]) {
  background: rgb(var(--brand-grey-dimmer));
  color: rgb(var(--brand-focus-strong));
  text-shadow: 0px 0px 10px rgba(255, 255, 255, 0.5);
}

:host([error]) {
  background: rgb(var(--brand-grey-dimmer));
  color: rgb(var(--brand-alert));
  text-shadow: 0px 0px 10px rgba(255, 255, 255, 0.5);
}

.frame {
  display: table-cell;
  flex: 0 0 100%;
  align-self: top;
  justify-self: left;
  vertical-align: top;
  text-align: left;
  height: 100%;
}
.frame.justify-page-top {
  vertical-align: top;
  align-self: flex-start;
}
.frame.justify-page-middle {
  vertical-align: middle;
  align-self: center;
  align-content: center;
}
.frame.justify-page-bottom {
  vertical-align: bottom;
  align-self: flex-end;
}
.frame.justify-line-left {
  text-align: left;
}
.frame.justify-line-center {
  text-align: center;
}
.frame.justify-line-right {
  text-align: right;
}
.frame .text-line, .frame .fake-break {
  flex: 1 1 100%;
  display: block;
  padding: 0;
  margin: 0;
  width: 100%;
}
.frame .frame-graphic {
  max-width: 100%;
  max-height: 100%;
}
`,
		];
	}

	override willUpdate(props: PropertyValues): void {
		super.willUpdate(props);
		this.error = !this.frame && !this.loading;
	}

	override updated(props: PropertyValues<this>): void {
		if (props.has('color') && this.color) {
			this.style.setProperty('color', this.color);
		}
	}

	public renderFrame(frame: FrameDto): TemplateResult {
		if (this.image) {
			return html`<div class="frame justify-line-center justify-page-middle">
				<img class="frame-graphic" src="${this.image}" />
			</div>`;
		}

		if (isValidNumber(frame.graphicId) && !this.image) {
			this.useRedText = true;
			return frameMultitagTextToTemplateResult('[jp3][jl3][nl]missing graphic[nl]');
		}

		if (frame.text && frame.htmlDescription?.includes('text-align:center')) {
			frame.text = `[jl3]${frame.text}`;
		}

		return frameMultitagTextToTemplateResult(frame.text ?? '[jp3][jl3][nl]preview error[nl]');
	}

	override render(): TemplateResult {
		if (this.loading) {
			return html`<div class="frame justify-line-center justify-page-middle">
				Loading Preview...
			</div>`;
		}
		if (!this.frame || this.error) {
			return html`<div class="frame justify-line-center justify-page-middle">
				Error generating preview
			</div>`;
		}
		return html`${this.renderFrame(this.frame)}`;
	}
}
